import {
    chakra,
    shouldForwardProp,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Portal,
    PopoverCloseButton,
    Icon,
    DrawerCloseButton,
    CloseButton,
    InputRightElement,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    TabIndicator,
    Spinner,
    Tooltip,
    CircularProgress,
    Image,
    Tag
} from '@chakra-ui/react';
import { InputGroup, InputLeftElement, Input } from '@chakra-ui/input';
import { Button } from '@chakra-ui/button';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, isValidMotionProp, motion } from 'framer-motion';
import commentBg from '../Utils/commentBg';
import {
    BookmarkMarkIcon,
    CloseCircleIcon,
    CommentIcon,
    FolderIcon,
    LinkIcon,
    MotionBox,
    SearchIcon
} from './UiElements';
import layoutStore from '../stores/layoutStore';
import { useHotkeys } from 'react-hotkeys-hook';
import useSearch from '../stores/searchStore';
import useDebounce from '../Utils/useDebounce';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { tooltipAnimation, tooltipDownAnimation } from './customTheme';
import { ArrowDownLeftIcon } from '@heroicons/react/24/solid';
import { LocalIconProvider } from './LocalIconProvider';
import FavIcon from './FavIcon';
import useStore from '../stores/store';
import { showToast } from '../Utils/showToastWrapper';
import { PLATFORM } from '../../platformFiles/utils/platformConstants';
import { openLink } from '../Utils/tabUtils';

const ChakraInput = chakra(motion.div, {
    shouldForwardProp: (prop) =>
        isValidMotionProp(prop) || shouldForwardProp(prop)
});

const searchModalW = '714px';

const SearchModal = () => {
    const setFilterText = useSearch((state) => state.setFilterText);
    const moveSelection = useSearch((state) => state.moveSelection);
    const handleEnter = useSearch((state) => state.handleEnter);
    const handleKeyPressStore = useSearch((state) => state.handleKeyPress);
    const currentFilter = useSearch((state) => state.currentFilter);
    const itemsToShow = useSearch((state) => state.itemsToShow);
    const setSelectedIndex = useSearch((state) => state.setSelectedIndex);
    const selectedIndex = useSearch((state) => state.selectedIndex);

    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const dockShadow = useColorModeValue(
        'drop-shadow(0 6px 8px rgba(9,26,50,0.22))',
        'drop-shadow(0 6px 8px rgba(19,20,24,0.92))'
    );
    const pickerBg = useColorModeValue('rgba(244, 245, 247, 1)', '#212528');
    const tagBg = useColorModeValue('gray.200', '#35393b');
    const showSearchModal = layoutStore((state) => state.showSearchModal);
    const setShowSearchModal = layoutStore((state) => state.setShowSearchModal);
    const close = () => setShowSearchModal(false);

    useHotkeys(
        'esc',
        (e) => {
            e.preventDefault(); //Always add this!
            close();
        },
        []
    );

    const filters = [
        { title: 'Current workspace', key: 'current' },
        { title: 'All workspaces', key: 'all' },
        { title: 'Open tabs', key: 'active' },
        { title: 'Bookmarks', key: 'bookmarks' }
    ];

    const searchInputRef = useRef(null);
    const reFocusSearchInput = (e) => {
        e.stopPropagation();
        searchInputRef?.current.focus();
    };

    const keyDownHandler = (e) => {
        handleKeyPressStore(null);
        if (e.key === 'Escape') {
            close();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            handleEnter();
        } else if (e.key === 'ArrowUp') {
            //If ctrlKey -> mvove to top
            if (e.altKey || e.metaKey) {
                e.preventDefault();
                e.stopPropagation();
                moveToTheTop();
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            moveUp();
        } else if (e.key === 'ArrowDown') {
            //If ctrlKey -> mvove to bottom
            if (e.altKey || e.metaKey) {
                e.preventDefault();
                e.stopPropagation();
                moveToTheBottom();
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            moveDown();
        }
    };

    //const itemLanesRef = useRef(null);
    const panelIndices = [0, 1, 2, 3];
    //Create one ref for each filter
    const virtuosoRefs = panelIndices.map(() =>
        React.createRef<VirtuosoHandle>()
    );

    const moveUp = () => {
        moveSelection(-1);
        let newIndex = selectedIndex - 1;
        scrollBy(newIndex, 'center', 'smooth');
    };

    const moveDown = () => {
        moveSelection(1);
        let newIndex = selectedIndex + 1;
        scrollBy(newIndex, 'center', 'smooth');
    };

    const moveToTheTop = () => {
        setSelectedIndex(0);
        scrollToTop();
    };

    const moveToTheBottom = () => {
        let howManyItems = itemsToShow.length;
        //let scrollHowLong = howManyItems * 58 + 16;
        moveSelection(howManyItems);
        scrollBy(howManyItems - 1);
    };

    const scrollToTop = () => {
        scrollBy(0);
    };

    const scrollBy = (itemToScrollTo: number, a = 'start', b = 'auto') => {
        /* itemLanesRef.current.scrollBy({
            top: pix,
            behavior: smooth ? 'smooth' : 'auto'
        }); */
        const filterToIndexMap = {
            current: 0,
            all: 1,
            active: 2,
            bookmarks: 3
        };

        const currentFilterIndex = filterToIndexMap[currentFilter];

        virtuosoRefs[currentFilterIndex]?.current?.scrollToIndex({
            index: itemToScrollTo,
            align: a as 'center' | 'end' | 'start',
            behavior: b as 'auto' | 'smooth'
        });
    };

    /* useEffect(() => {
        scrollToTop();
    }, [currentFilter]); */

    useEffect(() => {
        setFilterText('');
    }, [showSearchModal]);

    const [showClearButton, setShowClearButton] = useState(false);

    const searchMiddleware = (e) => {
        setShowClearButton(e.target.value.length > 0);
        setFilterText(e.target.value?.toLocaleLowerCase());
    };

    const clearInput = () => {
        setFilterText('');
        setShowClearButton(false);
        if (searchInputRef.current) {
            searchInputRef.current.value = '';
        }
    };

    return (
        <Box
            bg="rgba(0,0,0,0.34)"
            style={{ backdropFilter: 'blur(3px)' }}
            pos="fixed"
            left="0"
            right="0"
            top="0"
            h="100vh"
            w="100vw"
            alignItems="center"
            justifyContent="center"
            zIndex="38"
            onClick={close}
            cursor={'pointer'}
        >
            <AnimatePresence>
                {showSearchModal && (
                    <>
                        <MotionBox
                            onClick={(e) => e.stopPropagation()}
                            m="44px auto 16px"
                            p="16px"
                            w={searchModalW}
                            rounded={'14px'}
                            bg={menuBg}
                            filter={dockShadow}
                            initial={{
                                scale: 1.25 /* scaleX: 0.5, scaleY: 0.8 */
                            }}
                            animate={{ scale: 1 /* scaleX: 1, scaleY: 1 */ }}
                            exit={{ scaleX: 0.4, scaleY: 0.8 }}
                        >
                            <InputGroup
                                size="lg"
                                rounded="10px"
                                variant="filled"
                                //mt="8px"
                            >
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={SearchIcon} />
                                </InputLeftElement>
                                <Input
                                    ref={searchInputRef}
                                    type="tel"
                                    placeholder="Search"
                                    //size="lg"
                                    w="100%"
                                    rounded="10px"
                                    pr="136px"
                                    autoFocus
                                    onKeyDown={keyDownHandler}
                                    onChange={searchMiddleware}
                                    //focusBorderColor="#5ba6ff" //"#67B4ED" //"#028CFF"
                                />
                                <InputRightElement
                                    mr="12px"
                                    w={showClearButton ? '32px' : '120px'}
                                >
                                    {showClearButton ? (
                                        <Tooltip
                                            label="Clear input"
                                            zIndex="500000"
                                            placement="top"
                                            rounded="6px"
                                            motionProps={tooltipAnimation}
                                        >
                                            <Button
                                                onClick={clearInput}
                                                size="xs"
                                                rounded="8px"
                                                p="0"
                                            >
                                                <CloseCircleIcon />
                                            </Button>
                                        </Tooltip>
                                    ) : (
                                        <Text
                                            fontSize="15px"
                                            fontWeight={500}
                                            color="gray.500"
                                            opacity={0.5}
                                        >
                                            ↑↓ to navigate
                                        </Text>
                                    )}
                                </InputRightElement>
                            </InputGroup>
                        </MotionBox>
                        <MotionBox
                            onClick={(e) => e.stopPropagation()}
                            m="0px auto 24px"
                            p="16px 0 0"
                            w={searchModalW}
                            rounded={'14px'}
                            bg={pickerBg}
                            filter={dockShadow}
                            initial={{ opacity: 0, y: -44 }}
                            animate={{
                                opacity: 1,
                                y: 0,
                                transition: { delay: 0.12 }
                            }}
                            exit={{ opacity: 0, y: -36 }}
                        >
                            <TabsWrapper
                                filters={filters}
                                reFocusSearchInput={reFocusSearchInput}
                            >
                                <TabPanels
                                    maxH="70vh"
                                    minH="70vh"
                                    //overflowY="auto"
                                    borderTop="var(--chakra-colors-teBorder)"
                                    bg={menuBg}
                                    //px="16px"
                                    roundedBottom={'14px'}
                                >
                                    {panelIndices.map((index) => (
                                        <TabPanel key={index} p="0">
                                            <SearchResults
                                                ref={virtuosoRefs[index]}
                                            />
                                        </TabPanel>
                                    ))}
                                </TabPanels>
                                <Tag
                                    size="sm"
                                    pos="absolute"
                                    top="16px"
                                    right="16px"
                                    fontWeight={600}
                                    bg={tagBg}
                                    color={'#3690fb'}
                                    //colorScheme={'blue'}
                                    //variant="solid"
                                >
                                    <span style={{ opacity: 0.775 }}>
                                        Result:{' '}
                                    </span>
                                    {itemsToShow?.length
                                        ? itemsToShow.length
                                        : '-'}
                                </Tag>
                            </TabsWrapper>
                        </MotionBox>
                    </>
                )}
            </AnimatePresence>
        </Box>
    );
};

export default SearchModal;

const TabsWrapper = ({ filters, reFocusSearchInput, children }) => {
    const currentFilter = useSearch((state) => state.currentFilter);
    const setCurrentFilter = useSearch((state) => state.setCurrentFilter);

    const hoverButtonBg = useColorModeValue('gray.50', '#FFFFFF10');
    const activeButtonBg = useColorModeValue('gray.100', '#FFFFFF18');
    const title = useColorModeValue('gray.500', 'gray.600');
    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const bgActive = useColorModeValue(
        'var(--chakra-colors-chakra-border-color)',
        '#2a2e30'
    );

    // Map currentFilter to tabIndex
    const tabIndex = filters.findIndex(
        (filter) => filter.key === currentFilter
    );
    let disabledTabs = PLATFORM === 'web' ? ['bookmarks', 'active'] : [];

    // Update currentFilter when tabIndex changes
    const handleTabChange = (index) => {
        let key = filters[index].key;
        if (disabledTabs.includes(key)) {
            showToast(
                'Not available in web version',
                `Please add the browser extension to access ${filters[index].title}.`,
                'info'
            );
            return;
        }
        setCurrentFilter(filters[index].key);
    };

    return (
        <Tabs
            position="relative"
            variant="unstyled"
            index={tabIndex}
            onChange={handleTabChange}
        >
            <TabList pt="4px" px="16px">
                {filters.map((alt, index) => (
                    <Tab
                        p="12px 16px"
                        key={index}
                        tabIndex={-1}
                        onClick={reFocusSearchInput}
                        onMouseDown={(e) => e.preventDefault()}
                        roundedTop="10px"
                        transition={'0.1s'}
                        fontWeight={600}
                        fontSize={'14px'}
                        color={
                            //selected or not
                            tabIndex === index ? '#3690fb' : title
                        }
                        opacity={disabledTabs.includes(alt.key) ? 0.5 : ''}
                        _hover={{ color: '#3690fb' }}
                        _active={{
                            bg: bgActive
                        }}
                        borderTop={
                            tabIndex === index
                                ? 'var(--chakra-colors-teBorder)'
                                : '1px solid transparent'
                        }
                        borderLeft={
                            tabIndex === index
                                ? 'var(--chakra-colors-teBorder)'
                                : '1px solid transparent'
                        }
                        borderRight={
                            tabIndex === index
                                ? 'var(--chakra-colors-teBorder)'
                                : '1px solid transparent'
                        }
                        bg={tabIndex === index ? menuBg : 'transparent'}
                        mb="-1px"
                    >
                        {alt.title}
                    </Tab>
                ))}
            </TabList>
            {/* <TabIndicator
                mt="-1px"
                height="2px"
                bg="#5ebbff" //"#028CFF"
                borderRadius="1px"
                rounded="4px"
            /> */}
            {children}
        </Tabs>
    );
};

/* const useFuzzySearch = (dataSetToSearch, filterText) => {
    const debouncedSearchTerm = useDebounce(filterText, 200);
    const [itemsToShow, setItemsToShow] = useState(dataSetToSearch);

    const normalize = (str) =>
        str.toLowerCase().replace(/[\-\[\]{}()*+?.,\\\^$|#\s]/g, '\\$&');

    useEffect(() => {
        if (dataSetToSearch !== null) {
            if (debouncedSearchTerm === '') {
                setItemsToShow(dataSetToSearch);
            } else {
                // Create regex pattern with optional characters in between search characters
                const searchTermRegex = new RegExp(
                    [...normalize(debouncedSearchTerm)].join('.*'),
                    'i'
                );

                let filteredItems = dataSetToSearch.filter(
                    (el) =>
                        searchTermRegex.test(normalize(el.title)) ||
                        searchTermRegex.test(normalize(el.url))
                );

                setItemsToShow(filteredItems);
            }
        } else {
            setItemsToShow(null);
        }
    }, [dataSetToSearch, debouncedSearchTerm]);

    return itemsToShow;
}; */

const SearchResults = forwardRef((props, ref) => {
    const title = useColorModeValue('#828b9a', '#828b9a');

    const currentFilter = useSearch((state) => state.currentFilter);
    const dataSetToSearch = useSearch(
        (state) => state.currentSearchData[currentFilter]
    );
    const setSelectedIndex = useSearch((state) => state.setSelectedIndex);
    const itemsToShow = useSearch((state) => state.itemsToShow);
    const setItemsToShow = useSearch((state) => state.setItemsToShow);
    const getSearchInitData = useSearch((state) => state.getSearchInitData);
    const showSearchModal = layoutStore((state) => state.showSearchModal);
    const filterText = useSearch((state) => state.filterText);

    //const [itemsToShow, setItemsToShow] = useState(null);
    const debouncedSearchTerm = useDebounce(filterText, 250);

    const filteredItems = useMemo(() => {
        if (!dataSetToSearch || filterText === '') {
            return dataSetToSearch;
        }
        return dataSetToSearch.filter(
            (el) =>
                el.title?.toLowerCase().includes(filterText) ||
                el.url?.toLowerCase().includes(filterText) ||
                el.comment?.toLowerCase().includes(filterText)
        );
    }, [dataSetToSearch, debouncedSearchTerm]);

    useEffect(() => {
        setSelectedIndex(0);
        setItemsToShow(filteredItems);
    }, [filteredItems]);

    useEffect(() => {
        if (showSearchModal === true) {
            if (!dataSetToSearch) {
                //TODO change 'current' here to loaded user preference(from persist store)
                getSearchInitData();
            }
        }
    }, []);

    return (
        <>
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentFilter}
                    initial={{
                        y: itemsToShow?.length === 0 ? 0 : 10,
                        opacity: 0
                    }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 10, opacity: 0 }}
                    transition={{
                        opacity: { delay: 0.1, duration: 0.08 }
                    }}
                    tabIndex={-1}
                    style={{
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '70vh'
                    }}
                >
                    {itemsToShow === null ? (
                        <Spinner size="md" color="blue.300" mx="auto" />
                    ) : itemsToShow.length === 0 ? (
                        <motion.p
                            animate={{
                                x: [8, -8, 6, -6, 4, 0]
                            }}
                            transition={{ delay: 0.1 }}
                            style={{
                                fontSize: '16px',
                                color: title,
                                opacity: 0.75,
                                fontWeight: '500',
                                margin: '0 auto',
                                userSelect: 'none'
                            }}
                        >
                            No match
                        </motion.p>
                    ) : (
                        <Virtuoso
                            ref={ref as React.RefObject<VirtuosoHandle>}
                            //ref={virtuoso}
                            style={{
                                height: 'calc(70vh - 2px)',
                                width: '100%', //'56vw',
                                display: 'flex',
                                flexDirection: 'column',
                                scrollbarGutter: 'stable'
                            }}
                            fixedItemHeight={72}
                            overscan={100}
                            data={itemsToShow}
                            itemContent={(index, item) => (
                                <SearchModalItem
                                    key={item.id}
                                    index={index}
                                    item={item}
                                />
                            )}
                            components={{
                                Header: () => {
                                    return (
                                        <div
                                            style={{
                                                padding: '8px'
                                            }}
                                        />
                                    );
                                },
                                Footer: () => {
                                    return (
                                        <div
                                            style={{
                                                padding: '8px'
                                            }}
                                        />
                                    );
                                }
                            }}
                        />
                    )}
                </motion.div>
            </AnimatePresence>
        </>
    );
});

const SearchModalItem = ({ index, item }) => {
    //const bg = useColorModeValue('gray.50', '#313538');
    //const menuBg = useColorModeValue('#FFF', '#262A2D');
    //const dayColor = useColorModeValue('gray.500', '#A1A2A4');
    //const monthColor = useColorModeValue('gray.400', '#A1A2A4');
    //const timeLineColor = useColorModeValue('gray.100', '#3C4042');
    const icon = useColorModeValue('gray.600', '#A1A2A4');
    const urlText = useColorModeValue('gray.400', '#A1A2A4');
    const textComment = useColorModeValue('gray.700', '#FFFFFF');
    const spacesBG = useColorModeValue('#F7FAFC', '#35393c');

    const selectedIndex = useSearch((state) => state.selectedIndex);
    const filterText = useSearch((state) => state.filterText);
    const currentFilter = useSearch((state) => state.currentFilter);
    const setSelectedIndex = useSearch((state) => state.setSelectedIndex);
    const handleEnter = useSearch((state) => state.handleEnter);

    const [isLoading, setLoading] = useState(false);
    const [onHover, setHover] = useState(false);
    const onHoverHandler = () => {
        setSelectedIndex(index);
        setHover(true);
    };

    let isSelected = selectedIndex === index;

    const typeOfItem = item.url
        ? item.comment?.length
            ? 'tabWithNote'
            : 'tab'
        : item.isStacked
        ? 'stack'
        : 'note';

    let title = item.title
        ? item.title
        : typeOfItem.charAt(0).toUpperCase() + typeOfItem.slice(1);

    const clickMiddleware = () => {
        setLoading(true);
        handleEnter();
    };

    const fallbackSrc = useColorModeValue(
        '../../assets/images/fallbackSvg.svg',
        '../../assets/images/fallbackSvgDark.svg'
    );

    let favIconSrc = item.favIcon
        ? item.favIcon === 'localIcon'
            ? LocalIconProvider(item.title)
            : item.favIcon
        : item.favIconUrl
        ? item.favIconUrl
        : fallbackSrc;

    let SYMBOL = {
        group: (
            <Text fontSize="28px" mr="16px" maxW="28px">
                {item.emoji}
            </Text>
        ),
        groupCustomIcon: (
            <Box mr="16px" maxW="28px" maxH="28px" pos="relative">
                <Image
                    src={item.emoji}
                    boxSize="28px"
                    minW="28px"
                    overflow="hidden"
                    rounded="3px"
                />
                {isLoading ? (
                    <Flex pos="absolute" h="100%" w="100%">
                        <CircularProgress
                            transform={isLoading ? 'scale(1)' : 'scale(3)'}
                            opacity={isLoading ? '1' : '0'}
                            transition="0.4s 0.5 linear"
                            m="auto"
                            fontSize={'28px'}
                            isIndeterminate
                            color="blue.400"
                        ></CircularProgress>
                    </Flex>
                ) : (
                    ''
                )}
            </Box>
        ),
        note: <Icon fontSize="28px" mr="16px" as={CommentIcon} color={icon} />,
        stack: <StackOfTabsIcon stackedItems={item.stackedItems || []} />,
        tab: (
            <FavIcon
                url={item.url}
                favIcon={favIconSrc}
                my={''}
                loading={isLoading}
            />
        )
    };
    let SUBTITLECONTENT = {
        //group: items + (items === 1 ? ' Item' : ' Items'),
        //groupCustomIcon: items + (items === 1 ? ' Item' : ' Items'),
        note: item.comment,
        stack:
            item.stackedItems?.length +
            (item.stackedItems === 1 ? ' Item' : ' Items'),
        tab: item.url
    };
    let BUTTONTEXT = {
        group:
            currentFilter === 'current' || currentFilter === 'all'
                ? 'Go to'
                : 'Open all',
        groupCustomIcon:
            currentFilter === 'current' || currentFilter === 'all'
                ? 'Go to'
                : 'Open all',
        note: 'Go to',
        stack:
            currentFilter === 'current' || currentFilter === 'all'
                ? 'Go to'
                : 'Open all',
        tab:
            currentFilter === 'current' || currentFilter === 'all'
                ? 'Go to'
                : 'Open site'
    };

    //currentFilter === 'current' && currentFilter === 'all'

    let ICONTOSHOW = {
        group: <FolderIcon />,
        groupCustomIcon: <FolderIcon />,
        note: item.textCopied ? <BookmarkMarkIcon /> : <CommentIcon />,
        stack: <FolderIcon />,
        tab: <LinkIcon color={'currentColor'} />
    };

    const rowH = '64px';

    const openLinkMiddle = (e) => {
        openLink(e, item.url, setLoading);
    };

    return (
        <Flex p="4px 10px 4px 18px">
            {/* <Box>
                <Text color={dayColor} fontSize="14px" fontWeight={500}>
                    Fri
                </Text>
                <Text fontSize="18px" fontWeight={500}>
                    24
                </Text>
                <Text color={monthColor} fontSize="12px" fontWeight={600}>
                    Mar
                </Text>
            </Box>
            <Box ml="16px" h={rowH} w="4px" rounded="4px" bg={timeLineColor} /> */}
            <Tooltip
                placement="bottom"
                motionProps={tooltipDownAnimation}
                zIndex="1000"
                rounded="6px"
                px="12px"
                py="8px"
                openDelay={160}
                label={<TooltipContent item={item} />}
                isDisabled={
                    currentFilter !== 'current' && currentFilter !== 'all'
                }
            >
                <Flex
                    onMouseEnter={() => onHoverHandler()}
                    onMouseLeave={() => setHover(false)}
                    //ml="16px"
                    h={rowH}
                    //bg={isSelected ? menuBg : bg}
                    transition={'0s'}
                    rounded="12px"
                    flex="1"
                    boxShadow={
                        isSelected
                            ? '0 0 0 2px #5ba6ff, 0 12px 18px -10px rgba(49,144,255,0.35)'
                            : ''
                    }
                    alignItems={'center'}
                    pl="16px"
                    pr="56px"
                    maxW="100%"
                    pos="relative"
                >
                    {typeOfItem === 'note' ? (
                        <>
                            {/* {item.todo ? (
                            <TodoCheck
                                isDone={
                                    item.doneStatus
                                        ? item.doneStatus
                                        : false
                                }
                                commentColor={
                                    item.commentColor
                                        ? item.commentColor
                                        : false
                                }
                                toggleDone={toggleDone}
                                compactView={false}
                                reminder={'10px'}
                            />
                        ) : (
                            ''
                        )} */}
                            <Box
                                textDecoration={
                                    item.doneStatus ? 'line-through' : 'initial'
                                }
                                paddingTop="8px"
                                paddingBottom="8px"
                                px="12px"
                                flex="1"
                                //ml={item.todo ? '10px' : '0px'}
                                wordBreak="break-word"
                                fontStyle={
                                    item.textCopied === 'textCopied'
                                        ? 'italic'
                                        : 'normal'
                                }
                                fontSize="14px"
                                fontWeight="500"
                                whiteSpace="pre-line"
                                rounded="8px"
                                minHeight="36px"
                                color={
                                    item.commentColor !== false &&
                                    item.commentColor !== undefined
                                        ? commentBg[item.commentColor]?.text
                                        : textComment
                                }
                                bg={
                                    item.commentColor !== false &&
                                    item.commentColor
                                        ? commentBg[item.commentColor]?.bg
                                        : spacesBG
                                }
                                style={{
                                    fontStyle:
                                        item.textCopied === 'textCopied'
                                            ? 'italic'
                                            : 'normal'
                                }}
                                isTruncated
                            >
                                {item.comment}
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box my="auto">
                                {SYMBOL[typeOfItem] || SYMBOL['tab']}
                            </Box>
                            <Box w="100%" maxW="calc(100% - 54px)">
                                <Text
                                    fontSize="14px"
                                    fontWeight="500"
                                    wordBreak="break-all"
                                    isTruncated
                                    letterSpacing={'0.42px'}
                                >
                                    <Highlighted
                                        text={
                                            typeOfItem === 'stack'
                                                ? 'Stacked sites'
                                                : title
                                        }
                                        highlight={filterText}
                                    />
                                </Text>
                                {typeOfItem === 'tab' ||
                                typeOfItem === 'tabWithNote' ? (
                                    <Text
                                        //_hover={{ textDecoration: "underline" }}
                                        _hover={{
                                            textDecoration: 'underline'
                                        }}
                                        //as="a"
                                        //href={url}
                                        onClick={openLinkMiddle}
                                        onDragStart={(e) => {
                                            e.preventDefault();
                                        }}
                                        display="block"
                                        mt="2px"
                                        my="auto"
                                        color={urlText}
                                        fontWeight="500"
                                        fontSize="12px"
                                        isTruncated
                                        cursor={'pointer'}
                                    >
                                        <Highlighted
                                            text={
                                                item.url?.replace(
                                                    /(^\w+:|^)\/\//,
                                                    ''
                                                ) || '-'
                                            }
                                            highlight={filterText}
                                        />
                                    </Text>
                                ) : (
                                    <Text
                                        fontSize="12px"
                                        color={urlText}
                                        wordBreak="break-all"
                                        isTruncated
                                        mr="16px"
                                    >
                                        <Highlighted
                                            text={
                                                SUBTITLECONTENT[typeOfItem] ||
                                                SUBTITLECONTENT['tab']
                                            }
                                            highlight={filterText}
                                        />
                                    </Text>
                                )}
                            </Box>
                        </>
                    )}
                    <AnimatePresence initial={false}>
                        {isSelected && (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, x: -8, scale: 0.8 }}
                                animate={{
                                    opacity: 1,
                                    scale: 1,
                                    x: 0,
                                    transition: { delay: 0.03 }
                                }}
                                exit={{
                                    opacity: 0,
                                    x: -8,
                                    scale: 0.8,
                                    transition: { delay: 0 }
                                }}
                                style={{
                                    //margin: 'auto 4px auto auto',
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px'
                                }}
                            >
                                <Tooltip
                                    label={
                                        BUTTONTEXT[typeOfItem] ||
                                        BUTTONTEXT['tab']
                                    }
                                    zIndex="500000"
                                    placement="top"
                                    rounded="6px"
                                    motionProps={tooltipAnimation}
                                    openDelay={300}
                                >
                                    <Button
                                        size="sm"
                                        rounded="8px"
                                        onClick={clickMiddleware}
                                        fontSize={'14px'}
                                        maxW="32px"
                                        h="32px"
                                        pt="2px"
                                        //border="var(--chakra-colors-teBorder)"
                                        //bg='linear-gradient(180deg, #F7F9FA 0%, #F1F4F8 54%, #E4EAF2 100%)'
                                    >
                                        ↵
                                    </Button>
                                </Tooltip>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Flex>
            </Tooltip>
        </Flex>
    );
};

const TooltipContent = ({ item }) => {
    const itemsToShow = useSearch((state) => state.itemsToShow);
    const allWSdata = useSearch((state) => state.allWSdata);
    const currentFilter = useSearch((state) => state.currentFilter);
    const setGoToDetails = useSearch((state) => state.setGoToDetails);
    const currentWsId = useStore((state) => state.currentWsId);
    const workspaces = useStore((state) => state.userData.workspaces);
    const tabGroups = useStore((state) => state.tabGroups);
    const currentWsIndex = workspaces.findIndex((ws) => ws.id === currentWsId);
    const currentWs = workspaces[currentWsIndex];

    const [tabInWS, setInWS] = useState({ emoji: '', name: '', category: '' });
    const [tabInSpace, setTabInSpace] = useState(null);

    const findDetails = () => {
        if (currentFilter === 'current') {
            let info = tabGroups.find((group) =>
                group?.tabs?.some((sum) => sum.id === item.id)
            );
            if (!info) {
                return;
            }
            setTabInSpace(info);
            let inWS = currentWs;
            let inCategoryIndex = inWS?.catData
                ? inWS.catData.findIndex((cat) => cat.id === info.categoryID)
                : -1;
            let inCategoryName =
                inCategoryIndex !== -1
                    ? inWS.catData[inCategoryIndex].name
                    : '';
            setInWS({
                emoji: inWS.emoji,
                name: inWS.name,
                category: inCategoryName
            });
            return;
        } else if (currentFilter === 'all') {
            let allWsGroups = allWSdata.flatMap((workspace) => {
                return workspace && Array.isArray(workspace.tabData)
                    ? workspace.tabData
                    : [];
            });
            let info = allWsGroups.find((group) =>
                group?.tabs?.some((sum) => sum.id === item.id)
            );
            if (!info) {
                return;
            }
            setTabInSpace(info);
            //find the info.
            let inWS = allWSdata.find((group) =>
                group.tabData.some((item) => item.id === info.id)
            );
            let inCategoryIndex = inWS.categories.findIndex(
                (cat) => cat.id === info.categoryID
            );
            let inCategoryName = inWS.categories[inCategoryIndex].name;
            let wsIndex = workspaces.findIndex((ws) => ws.id === inWS.WSid);
            let workspace = workspaces[wsIndex];
            let emoji = workspace?.emoji ?? '';
            let name = workspace?.name ?? '';
            setInWS({ emoji: emoji, name: name, category: inCategoryName });
            setGoToDetails({
                toWsId: inWS.WSid,
                inCategoryIndex: inCategoryIndex
            });
        }
    };

    useEffect(() => {
        findDetails();
    }, []);

    if (!tabInSpace) return null;

    return (
        <Box>
            <SavedInRow
                type={'Workspace: '}
                emoji={tabInWS?.emoji}
                name={tabInWS?.name}
            />
            <SavedInRow
                type={'Category: '}
                emoji={''}
                name={tabInWS?.category}
            />
            <SavedInRow
                type={'Group: '}
                emoji={tabInSpace?.emoji}
                name={tabInSpace?.title}
            />
        </Box>
    );
};

const SavedInRow = ({ type, emoji, name }) => {
    return (
        <Flex mt={type === 'Workspace: ' ? '0' : '4px'} fontSize="14px">
            <Text opacity="0.6" mr="4px">
                {type}
            </Text>
            {emoji && (
                <Text mr="8px">
                    {emoji?.length > 10 ? (
                        <Image
                            src={emoji}
                            minW="16px"
                            minH="16px"
                            maxW="16px"
                            maxH="16px"
                            overflow="hidden"
                            rounded="2px"
                            mt="4px"
                        />
                    ) : (
                        emoji
                    )}
                </Text>
            )}
            <Text fontWeight="600">{name}</Text>
        </Flex>
    );
};

const Highlighted = ({ text = '', highlight = '' }) => {
    if (!highlight.trim()) {
        return <span>{text}</span>;
    }
    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);

    return (
        <span>
            {parts.filter(String).map((part, i) => {
                return regex.test(part) ? (
                    <mark key={i} className="markedSearchQuery">
                        {part}
                    </mark>
                ) : (
                    <span key={i}>{part}</span>
                );
            })}
        </span>
    );
};

const StackOfTabsIcon = ({ stackedItems }) => {
    let show5 = stackedItems.slice(0, 3);
    const bgActive = useColorModeValue('#FFF', '#3c4042'); //'#3c4042'

    let xPos = {
        0: 2,
        1: -2,
        2: -6
    };

    let rotate = {
        0: 0,
        1: -6,
        2: -8
    };

    return (
        <div
            style={{
                position: 'relative',
                width: '32px',
                height: '32px',
                marginRight: '16px'
            }}
        >
            {show5.map((item, i) => {
                return (
                    <div
                        /* animate={{
                            x: xPos[i],
                            rotate: rotate[i]
                        }} */
                        key={i}
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            zIndex: 5 - i,
                            transform: `translateX(${xPos[i]}px) rotate(${rotate[i]}deg)`
                        }}
                    >
                        {i === 0 ? (
                            <FavIcon
                                key={i}
                                url={item.url}
                                favIcon={item.favIcon}
                                my={''}
                                isInStack={true}
                            />
                        ) : (
                            <Flex
                                transition="0.15s"
                                alignItems="center"
                                rounded={'8px'}
                                minWidth={'32px'}
                                minHeight={'32px'}
                                bg={bgActive}
                                boxShadow={'0 1px 2px 1px rgba(78,80,81,0.24)'}
                                pos="relative"
                                maxHeight={'32px'}
                                maxWidth={'32px'}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};
